import classNames from 'classnames';
import { useFormik } from 'formik';
import { isEqual, pick } from 'lodash';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Button } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { UpdateEntityRequest } from 'api';

interface CompanyCRSFormik {
  crs_status: string;
}

export const crsStatusItems = [
  {
    label: 'Non-Reporting Financial Institution',
    value: 'non-reporting',
  },
  {
    label: 'Other Active Non-Financial Entity',
    value: 'other-active',
  },
  {
    label: 'Passive Non-Financial Entity',
    value: 'passive',
  },
  {
    label: 'Government Entity, International Organisation and Central Bank',
    value: 'government',
  },
  {
    label:
      'A corporation, the stock of which is regularly traded on an established securities market',
    value: 'corporation',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

const initialValues: CompanyCRSFormik = {
  crs_status: crsStatusItems[0].value,
};

export const CompanyCRSHolder = ({
  moveForward,
  allowSkip,
  entity_type_prefix,
}: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const getInitialValues = () => {
    return {
      crs_status:
        dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix]?.crs_status ??
        initialValues.crs_status,
    };
  };

  const formik = useFormik<CompanyCRSFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    initialValues: getInitialValues(),
    onSubmit: async values => {
      setDirty(false);
      const init = pick(values, ['crs_status']);
      const entityTypeDetails = dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix];
      const current = pick(entityTypeDetails, ['crs_status']);
      if (!isEqual(init, current)) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            [entity_type_prefix]: {
              ...dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix],
              ...values,
            },
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeCRSStatus = (value: string) => {
    formik.setFieldValue('crs_status', value);
  };

  return (
    <OnboardingHolderWrapper
      title="Select a classification that matches your CRS status:"
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col gap-2">
        {crsStatusItems.map(({ label, value }, i) => (
          <Button
            key={'ButtonOptions_' + i}
            onClick={() => handleChangeCRSStatus(value)}
            variant="secondary"
            className={classNames({
              'border-[#486a7b]': formik.values.crs_status === value,
            })}
            textAlign="left"
          >
            {label}
          </Button>
        ))}
      </div>
    </OnboardingHolderWrapper>
  );
};
